<template>
    <div class="app-hub-page channel-center">
        <h1 class="app-hub-page-title">渠道订单</h1>
        <el-card class="channel-center__shell app-hub-card" shadow="never">
            <el-tabs
                v-model="activeTab"
                class="channel-center__tabs app-hub-tabs"
                type="border-card"
                @tab-click="handleTabClick"
            >
                <el-tab-pane label="渠道订单" name="orders" lazy>
                    <div class="channel-center__pane app-hub-pane">
                        <el-row :gutter="12" class="channel-center__stats app-hub-stats">
                            <el-col :xs="12" :sm="6">
                                <div class="app-hub-stat">
                                    <div class="app-hub-stat__label">订单总数</div>
                                    <div class="app-hub-stat__value">{{ orderStats.order_count }}</div>
                                </div>
                            </el-col>
                            <el-col :xs="12" :sm="6">
                                <div class="app-hub-stat app-hub-stat--muted">
                                    <div class="app-hub-stat__label">{{ channelAmountPayableSumLabel }}</div>
                                    <div class="app-hub-stat__value">¥ {{ formatMoney(orderStats.channel_payable_sum) }}</div>
                                </div>
                            </el-col>
                            <el-col :xs="12" :sm="6">
                                <div class="app-hub-stat app-hub-stat--muted">
                                    <div class="app-hub-stat__label">{{ channelAmountPaidSumLabel }}</div>
                                    <div class="app-hub-stat__value">¥ {{ formatMoney(orderStats.channel_paid_sum) }}</div>
                                </div>
                            </el-col>
                            <el-col :xs="12" :sm="6">
                                <div class="app-hub-stat app-hub-stat--accent">
                                    <div class="app-hub-stat__label">{{ channelAmountPendingSumLabel }}</div>
                                    <div class="app-hub-stat__value">¥ {{ formatMoney(orderStats.channel_pending_sum) }}</div>
                                </div>
                            </el-col>
                        </el-row>

                        <el-form :inline="true" size="small" class="channel-center__filters app-hub-filters" @submit.native.prevent="runOrderSearch">
                            <div class="channel-center__filters-left app-hub-filters__left">
                                <el-form-item label="综合">
                                    <el-input
                                        v-model.trim="orderFilters.keyword"
                                        clearable
                                        placeholder="公司名称、信用机构代码证搜索"
                                        class="channel-center__filter-input--wide app-search-input"
                                        @keyup.enter.native="runOrderSearch"
                                    />
                                </el-form-item>
                                <el-form-item v-if="!isSubaccountUser" label="渠道">
                                    <el-select
                                        v-model="orderFilters.channel_id"
                                        clearable
                                        filterable
                                        placeholder="全部"
                                        class="channel-center__filter-select"
                                    >
                                        <el-option
                                            v-for="c in channelOptions"
                                            :key="c.id"
                                            :label="channelOptionLabel(c)"
                                            :value="c.id"
                                        />
                                    </el-select>
                                </el-form-item>
                                <el-form-item v-if="isChannelSpecialist" label="结算">
                                    <el-select v-model="orderFilters.settlement_status" clearable placeholder="全部" class="channel-center__filter-select--narrow">
                                        <el-option label="未结清" value="unsettled" />
                                        <el-option label="已结清" value="settled" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item v-else-if="isSuperAdminUser" label="结算">
                                    <el-select v-model="orderFilters.settlement_status" clearable placeholder="全部" class="channel-center__filter-select--narrow">
                                        <el-option label="已结清" value="settled" />
                                        <el-option label="未结清" value="unsettled" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item v-else label="归档">
                                    <el-select v-model="orderFilters.is_archived" clearable placeholder="全部" class="channel-center__filter-select--narrow">
                                        <el-option label="未归档" :value="0" />
                                        <el-option label="已归档" :value="1" />
                                    </el-select>
                                </el-form-item>
                            </div>
                            <div class="channel-center__filters-right app-hub-filters__right">
                                <el-form-item class="channel-center__filters-actions app-hub-filters__actions">
                                    <el-button type="primary" @click="runOrderSearch">查询</el-button>
                                    <el-button @click="resetOrderFilters">重置</el-button>
                                </el-form-item>
                            </div>
                        </el-form>

                        <el-table
                            v-loading="ordersLoading"
                            :data="orders"
                            border
                            stripe
                            size="small"
                            class="channel-center__table app-hub-table"
                            :header-cell-style="{ textAlign: 'center' }"
                            :cell-style="{ textAlign: 'center' }"
                        >
                            <el-table-column label="日期" width="85" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ formatOrderDate(row) }}</template>
                            </el-table-column>
                            <el-table-column label="渠道名称" width="125" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ row.channel_name || (row.channel && row.channel.name) || '—' }}</template>
                            </el-table-column>
                            <el-table-column label="公司名称" width="260" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ row.company && row.company.name ? row.company.name : '—' }}</template>
                            </el-table-column>
                            <el-table-column label="统一信用代码" width="170" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{
                                    row.company && row.company.credit_code ? row.company.credit_code : '—'
                                }}</template>
                            </el-table-column>
                            <el-table-column label="税务进度" width="100" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ formatTaxProgressLabel(row) }}</template>
                            </el-table-column>
                            <el-table-column label="工商进度" width="100" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ formatBizProgressLabel(row) }}</template>
                            </el-table-column>
                            <el-table-column :label="channelAmountPayableColLabel" width="100" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">¥ {{ channelFee(row, 'payable') }}</template>
                            </el-table-column>
                            <el-table-column :label="channelAmountPaidColLabel" width="100" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">¥ {{ channelFee(row, 'paid') }}</template>
                            </el-table-column>
                            <el-table-column :label="channelAmountPendingColLabel" width="100" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">
                                    <span :class="{ 'channel-center__amount-warn': channelFeePending(row) > 0 }">¥ {{ channelFee(row, 'pending') }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column v-if="isChannelSpecialist" label="操作" width="120" align="center" header-align="center">
                                <template slot-scope="{ row }">
                                    <div class="channel-center__op-actions">
                                        <el-button
                                            type="text"
                                            size="small"
                                            :class="channelConfirmActionClass(row)"
                                            :disabled="!canConfirmCollection(row)"
                                            @click="confirmCollection(row)"
                                        >
                                            确认收款
                                        </el-button>
                                        <el-button
                                            type="text"
                                            size="small"
                                            :disabled="!canRemindCollection(row)"
                                            @click="remindCollection(row)"
                                        >
                                            催款
                                        </el-button>
                                    </div>
                                </template>
                            </el-table-column>
                            <el-table-column v-if="!isChannelSpecialist" label="操作" width="130" align="center" header-align="center">
                                <template slot-scope="{ row }">
                                    <div class="channel-center__op-actions">
                                        <el-button
                                            type="text"
                                            size="small"
                                            :class="channelFeeActionClass(row)"
                                            :disabled="isChannelFeeLocked(row)"
                                            @click="openChannelFeeDialog(row)"
                                        >
                                            费用登记
                                        </el-button>
                                        <el-button type="text" size="small" class="channel-center__danger" @click="confirmDeleteChannelOrder(row)">
                                            删除
                                        </el-button>
                                    </div>
                                </template>
                            </el-table-column>
                        </el-table>

                        <div v-if="orderMeta.total > 0" class="channel-center__pager app-pagination-antd-wrap app-hub-pager">
                            <el-pagination
                                background
                                layout="total, prev, pager, next, sizes"
                                popper-class="app-pagination-antd-sizes-popper"
                                :page-sizes="[10, 20, 50, 100]"
                                :current-page="orderPage"
                                :page-size="orderPerPage"
                                :total="orderMeta.total"
                                @current-change="handleOrderPageChange"
                                @size-change="handleOrderPageSizeChange"
                            />
                        </div>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="渠道管理" name="channels" lazy>
                    <div class="channel-center__pane app-hub-pane">
                        <div class="channel-center__toolbar">
                            <el-button v-if="canManageChannels" type="primary" size="small" icon="el-icon-plus" @click="openCreateChannelDialog">
                                新增渠道
                            </el-button>
                            <el-input
                                v-model.trim="channelKeyword"
                                size="small"
                                clearable
                                placeholder="搜索渠道名称、联系人 / 手机"
                                class="channel-center__search app-search-input"
                                @keyup.enter.native="noop"
                            >
                                <el-button slot="append" icon="el-icon-search" @click="noop" />
                            </el-input>
                            <el-button size="small" @click="resetChannelManageSearch">重置</el-button>
                        </div>
                        <el-table
                            v-loading="channelsLoading"
                            :data="filteredChannelRows"
                            border
                            stripe
                            size="small"
                            class="channel-center__table app-hub-table"
                            :header-cell-style="{ textAlign: 'center' }"
                            :cell-style="{ textAlign: 'center' }"
                        >
                            <el-table-column label="创建时间" min-width="168" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ formatDateTime(row.created_at) }}</template>
                            </el-table-column>
                            <el-table-column prop="name" label="渠道名称" min-width="160" show-overflow-tooltip />
                            <el-table-column prop="contact" label="联系人" min-width="100" show-overflow-tooltip />
                            <el-table-column prop="phone" label="手机号" min-width="120" show-overflow-tooltip />
                            <el-table-column label="对接专员" min-width="120" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{
                                    row.managed_by && (row.managed_by.name || row.managed_by.username)
                                        ? (row.managed_by.name || row.managed_by.username)
                                        : '—'
                                }}</template>
                            </el-table-column>
                            <el-table-column label="状态" min-width="88">
                                <template slot-scope="{ row }">
                                    <el-tag :type="channelStatusTag(row)" size="mini" effect="plain">
                                        {{ channelStatusLabel(row) }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column label="操作" width="160" fixed="right">
                                <template slot-scope="{ row }">
                                    <template v-if="canManageChannels">
                                        <el-button type="text" size="mini" @click="openEditChannelDialog(row)">编辑</el-button>
                                        <el-button type="text" size="mini" class="channel-center__danger" @click="confirmDeleteChannel(row)">
                                            删除
                                        </el-button>
                                    </template>
                                    <span v-else>—</span>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </el-tab-pane>
            </el-tabs>
        </el-card>

        <el-dialog
            :title="channelDialogTitle"
            :visible.sync="channelDialogVisible"
            width="560px"
            append-to-body
            custom-class="channel-center__dialog"
            @closed="resetChannelForm"
        >
            <el-form ref="channelFormRef" :model="channelForm" label-width="108px" size="small">
                <el-form-item label="渠道名称" required>
                    <el-input v-model.trim="channelForm.name" maxlength="80" placeholder="如：某某财税服务商" />
                </el-form-item>
                <el-form-item label="对接专员">
                    <el-select
                        v-model="channelForm.managed_by_user_id"
                        clearable
                        filterable
                        placeholder="选择渠道专员子账号（可选）"
                        class="channel-center__managed-select"
                    >
                        <el-option
                            v-for="u in channelSpecialistUsers"
                            :key="u.id"
                            :label="(u.name || u.username || '') + '（' + (u.username || '') + '）'"
                            :value="u.id"
                        />
                    </el-select>
                    <div class="channel-center__field-tip">
                        指定后，该专员可在「渠道订单」中查看关联本渠道的全部订单（与订单建档人无关）；不指定则其只能看到本人建档的渠道单。
                    </div>
                </el-form-item>
                <el-form-item label="联系人">
                    <el-input v-model.trim="channelForm.contact" maxlength="50" placeholder="姓名" />
                </el-form-item>
                <el-form-item label="联系电话">
                    <el-input v-model.trim="channelForm.phone" maxlength="11" placeholder="手机" />
                </el-form-item>
                <el-form-item label="启用">
                    <el-switch v-model="channelForm.status" :active-value="1" :inactive-value="0" />
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button @click="channelDialogVisible = false">取消</el-button>
                <el-button type="primary" :loading="channelSaving" @click="submitChannelForm">保存</el-button>
            </span>
        </el-dialog>

        <el-dialog
            title="费用登记"
            :visible.sync="channelFeeDialogVisible"
            width="480px"
            append-to-body
            @closed="resetChannelFeeForm"
        >
            <el-form label-width="100px" size="small">
                <el-form-item :label="channelFeeDialogPayableLabel">
                    <el-input-number
                        v-model="channelFeeForm.channel_payable_total"
                        :min="0"
                        :precision="2"
                        :step="100"
                        :controls="false"
                        class="channel-center__fee-input"
                    />
                </el-form-item>
                <el-form-item :label="channelFeeDialogPaidLabel">
                    <el-input-number
                        v-model="channelFeeForm.channel_paid_total"
                        :min="0"
                        :precision="2"
                        :step="100"
                        :controls="false"
                        class="channel-center__fee-input"
                    />
                </el-form-item>
                <el-form-item :label="channelFeeDialogPendingLabel">
                    <el-input-number
                        v-model="channelFeePendingModel"
                        :precision="2"
                        :controls="false"
                        disabled
                        class="channel-center__fee-input channel-center__fee-readonly"
                    />
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button @click="channelFeeDialogVisible = false">取消</el-button>
                <el-button type="primary" :loading="channelFeeSaving" @click="submitChannelFees">保存</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
export default {
    name: 'ChannelCenterPage',
    data() {
        return {
            activeTab: 'orders',
            channelKeyword: '',
            channelDialogVisible: false,
            channelDialogMode: 'create',
            editingChannelId: null,
            channelSaving: false,
            channelsLoading: false,
            channels: [],
            ordersLoading: false,
            orders: [],
            orderPage: 1,
            orderPerPage: 10,
            orderMeta: {
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0
            },
            orderStats: {
                order_count: 0,
                channel_payable_sum: 0,
                channel_paid_sum: 0,
                channel_pending_sum: 0,
                enabled_channels_count: 0
            },
            orderFilters: {
                keyword: '',
                channel_id: '',
                is_archived: '',
                settlement_status: ''
            },
            channelForm: {
                name: '',
                managed_by_user_id: null,
                contact: '',
                phone: '',
                status: 1
            },
            channelSpecialistUsers: [],
            channelFeeDialogVisible: false,
            channelFeeSaving: false,
            channelActionSubmittingId: null,
            channelFeeForm: {
                order_id: null,
                order_no: '',
                company_name: '',
                channel_name: '',
                channel_payable_total: 0,
                channel_paid_total: 0
            }
        };
    },
    computed: {
        isChannelSpecialist() {
            var u = this.$store.getters.currentUser;
            var r = u && u.roles ? u.roles : [];
            return r.indexOf('channel-specialist') >= 0;
        },
        isSubaccountUser() {
            var u = this.$store.getters.currentUser;
            return !!(u && !u.is_super_admin);
        },
        isSuperAdminUser() {
            var u = this.$store.getters.currentUser;
            return !!(u && u.is_super_admin);
        },
        canManageChannels() {
            var u = this.$store.getters.currentUser;
            var perms = u && u.permissions ? u.permissions : [];
            return !!(u && u.is_super_admin) || perms.indexOf('order.edit') >= 0;
        },
        channelAmountPayableSumLabel() {
            return this.isChannelSpecialist ? '应收合计' : '应付合计';
        },
        channelAmountPaidSumLabel() {
            return this.isChannelSpecialist ? '已收合计' : '已付合计';
        },
        channelAmountPendingSumLabel() {
            return this.isChannelSpecialist ? '待收合计' : '待付合计';
        },
        channelAmountPayableColLabel() {
            return this.isChannelSpecialist ? '应收' : '应付';
        },
        channelAmountPaidColLabel() {
            return this.isChannelSpecialist ? '已收' : '已付';
        },
        channelAmountPendingColLabel() {
            return this.isChannelSpecialist ? '待收' : '待付';
        },
        channelFeeDialogPayableLabel() {
            return this.isChannelSpecialist ? '应收渠道' : '应付渠道';
        },
        channelFeeDialogPaidLabel() {
            return this.isChannelSpecialist ? '已收渠道' : '已付渠道';
        },
        channelFeeDialogPendingLabel() {
            return this.isChannelSpecialist ? '待收渠道' : '待付渠道';
        },
        channelDialogTitle() {
            return this.channelDialogMode === 'edit' ? '编辑渠道' : '新增渠道';
        },
        filteredChannelRows() {
            var kw = (this.channelKeyword || '').trim().toLowerCase();
            var list = this.channels || [];
            if (!kw) {
                return list;
            }
            return list.filter(function (row) {
                var name = row.name != null ? String(row.name).toLowerCase() : '';
                var contact = row.contact != null ? String(row.contact).toLowerCase() : '';
                var phone = row.phone != null ? String(row.phone).toLowerCase() : '';
                return name.indexOf(kw) !== -1 || contact.indexOf(kw) !== -1 || phone.indexOf(kw) !== -1;
            });
        },
        channelOptions() {
            return this.channels || [];
        },
        /** 与应付/已付同组件，保证待付数值显示在输入框内 */
        channelFeePendingModel: {
            get: function () {
                var a = Number(this.channelFeeForm.channel_payable_total || 0);
                var b = Number(this.channelFeeForm.channel_paid_total || 0);
                return Math.round(Math.max(0, a - b) * 100) / 100;
            },
            set: function () {}
        }
    },
    watch: {
        '$route.query.tab': {
            immediate: true,
            handler: function () {
                this.syncTabFromRoute();
            }
        },
        activeTab: {
            immediate: true,
            handler: function (name) {
                if (name === 'channels') {
                    this.fetchChannels();
                }
                if (name === 'orders') {
                    this.fetchChannelCenterOrders();
                }
            }
        }
    },
    created() {
        this.fetchChannels();
    },
    methods: {
        noop() {},
        /** 渠道管理：清空本地搜索关键词，列表恢复为全部渠道 */
        resetChannelManageSearch() {
            this.channelKeyword = '';
        },
        channelOptionLabel(c) {
            if (!c) {
                return '';
            }
            var ownerName = c.managed_by && (c.managed_by.name || c.managed_by.username)
                ? (c.managed_by.name || c.managed_by.username)
                : (c.contact || '');
            var main = c.name || '';
            var label = ownerName ? (main + ' - ' + ownerName) : main;
            var tail = c.status === 0 || c.status === '0' ? '（停用）' : '';
            return label + tail;
        },
        formatOrderDate(row) {
            if (!row) {
                return '—';
            }
            var d = row.receipt_date;
            if (d && typeof d === 'string') {
                return d.length >= 10 ? d.slice(0, 10) : d;
            }
            if (row.created_at) {
                var s = String(row.created_at);
                return s.length >= 10 ? s.slice(0, 10) : s;
            }
            return '—';
        },
        formatTaxProgressLabel(row) {
            var n = row && row.tax_progress_status != null ? Number(row.tax_progress_status) : null;
            if (n === null || Number.isNaN(n)) {
                return '—';
            }
            var map = {
                1: '税务未开',
                3: '清税中',
                4: '已清税'
            };
            return map[n] || '—';
        },
        /**
         * 渠道中心列表：渠道单且工商仍为库表默认 101 时，在税务未清税完成前不展示「办理中」。
         * 含：税务未录入（与「税务进度」— 一致）、税务未开(1)、清税中(3)。已清税(4) 后走常规工商映射。
         */
        isChannelCenterNewOrderBizUnsettled(row) {
            var isChannel =
                Number(row.is_channel_order) === 1 ||
                (row.channel_id != null &&
                    String(row.channel_id) !== '' &&
                    !Number.isNaN(Number(row.channel_id)) &&
                    Number(row.channel_id) > 0);
            if (!isChannel) {
                return false;
            }
            if (Number(row.biz_progress_status) !== 101) {
                return false;
            }
            var taxRaw = row.tax_progress_status;
            if (taxRaw == null || String(taxRaw).trim() === '') {
                return true;
            }
            var taxN = Number(taxRaw);
            if (Number.isNaN(taxN)) {
                return true;
            }
            return taxN === 1 || taxN === 3;
        },
        /**
         * 与后端 OrderController::isOrderInRejectResubmitListScope 一致：审核中驳回后进入驳回单的渠道订单。
         */
        isOrderInRejectResubmitListScope(row) {
            if (!row || Number(row.is_archived) === 1) {
                return false;
            }
            if (Number(row.tax_progress_status) !== 4) {
                return false;
            }
            if (Number(row.biz_progress_status) !== 104) {
                return false;
            }
            var c = row.company;
            if (!c) {
                return true;
            }
            var d = c.announcement_end_date;
            return d == null || String(d).trim() === '';
        },
        /** 工商进度与 OrderWorkbench.displayBizProgressLabel 口径一致（含已归档/外部登记已注销） */
        formatBizProgressLabel(row) {
            if (!row) {
                return '—';
            }
            if (this.isOrderExternalRegDeregistered(row.external_reg_status)) {
                return '已注销';
            }
            if (Number(row.is_archived) === 1) {
                return '已注销';
            }
            if (this.isOrderInRejectResubmitListScope(row)) {
                return '已驳回';
            }
            if (this.isChannelCenterNewOrderBizUnsettled(row)) {
                return '—';
            }
            var announcement = Number(row.biz_announcement_status || 0);
            var annual = Number(row.annual_report_stage || 0);
            var license = Number(row.biz_license_status || 0);
            var progress = Number(row.biz_progress_status || 0);
            var uninitialized = announcement === 0 && annual === 0 && license === 9 && progress === 101;
            if (uninitialized) {
                return '—';
            }
            var n = row.biz_progress_status != null ? Number(row.biz_progress_status) : null;
            if (n === null || Number.isNaN(n)) {
                return '—';
            }
            var map = {
                101: '办理中',
                102: '公示中',
                103: '待提交',
                104: '异常'
            };
            return map[n] || '—';
        },
        /** 与后端 OrderController::isExternalRegStatusCancelled 一致：登记状态含「注销」 */
        isOrderExternalRegDeregistered(statusText) {
            if (statusText === null || statusText === undefined) {
                return false;
            }
            var s = String(statusText);
            return s.indexOf('注销') !== -1;
        },
        channelFee(row, part) {
            var f = row.financial;
            if (!f) {
                return this.formatMoney(0);
            }
            if (part === 'payable') {
                return this.formatMoney(f.channel_payable_total);
            }
            if (part === 'paid') {
                return this.formatMoney(f.channel_paid_total);
            }
            if (f.channel_pending_total != null) {
                return this.formatMoney(f.channel_pending_total);
            }
            var a = Number(f.channel_payable_total || 0);
            var b = Number(f.channel_paid_total || 0);
            return this.formatMoney(Math.max(0, a - b));
        },
        channelFeePending(row) {
            var f = row.financial;
            if (!f) {
                return 0;
            }
            if (f.channel_pending_total != null) {
                return Number(f.channel_pending_total);
            }
            var a = Number(f.channel_payable_total || 0);
            var b = Number(f.channel_paid_total || 0);
            return Math.max(0, a - b);
        },
        isChannelFeeLocked(row) {
            var u = this.$store.getters.currentUser;
            if (!u || !u.is_super_admin) {
                return false;
            }
            var f = row && row.financial ? row.financial : null;
            if (!f) {
                return false;
            }
            var payable = Number(f.channel_payable_total || 0);
            return payable !== 0 && this.channelFeePending(row) <= 0;
        },
        channelFeeActionClass(row) {
            return this.isChannelFeeLocked(row) ? 'channel-center__fee-action-locked' : '';
        },
        isOrderDeregistered(row) {
            if (!row) {
                return false;
            }
            if (Number(row.is_archived) === 1) {
                return true;
            }
            if (this.isOrderExternalRegDeregistered(row.external_reg_status)) {
                return true;
            }
            return Number(row.biz_progress_status || 0) === 103;
        },
        isCollectionConfirmed(row) {
            var f = row && row.financial ? row.financial : null;
            return !!(f && f.channel_receipt_confirmed_at);
        },
        isChannelConfirmSettled(row) {
            var f = row && row.financial ? row.financial : null;
            if (!f) {
                return false;
            }
            var payable = Number(f.channel_payable_total || 0);
            return payable !== 0 && this.channelFeePending(row) <= 0 && this.isCollectionConfirmed(row);
        },
        channelConfirmActionClass(row) {
            return this.isChannelConfirmSettled(row) ? 'channel-center__confirm-action-done' : '';
        },
        canConfirmCollection(row) {
            if (!this.isChannelSpecialist || !row) {
                return false;
            }
            if (this.isCollectionConfirmed(row)) {
                return false;
            }
            if (Number(row.is_archived) === 1) {
                return false;
            }
            var f = row && row.financial ? row.financial : null;
            var payable = Number(f && f.channel_payable_total != null ? f.channel_payable_total : 0);
            if (payable === 0) {
                return false;
            }
            return this.channelFeePending(row) <= 0;
        },
        canRemindCollection(row) {
            if (!this.isChannelSpecialist || !row) {
                return false;
            }
            if (this.isCollectionConfirmed(row)) {
                return false;
            }
            if (this.channelFeePending(row) <= 0) {
                return false;
            }
            if (Number(row.tax_progress_status || 0) !== 4) {
                return false;
            }
            if (!this.isOrderDeregistered(row)) {
                return false;
            }
            var base = row.external_reg_checked_at || row.updated_at || row.created_at;
            if (!base) {
                return false;
            }
            var ms = new Date(base).getTime();
            if (!Number.isFinite(ms)) {
                return false;
            }
            return (Date.now() - ms) >= (3 * 24 * 60 * 60 * 1000);
        },
        confirmCollection(row) {
            var _this = this;
            if (!row || !row.id || !this.canConfirmCollection(row) || this.channelActionSubmittingId) {
                return;
            }
            this.channelActionSubmittingId = row.id;
            window.axios.post('/orders/' + row.id + '/channel-confirm-receipt').then(function (res) {
                _this.$message.success((res.data && res.data.message) || '确认收款成功');
                _this.fetchChannelCenterOrders();
            }).catch(function (err) {
                var msg = err && err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : '确认收款失败';
                _this.$message.error(msg);
            }).finally(function () {
                _this.channelActionSubmittingId = null;
            });
        },
        remindCollection(row) {
            var _this = this;
            if (!row || !row.id || !this.canRemindCollection(row) || this.channelActionSubmittingId) {
                return;
            }
            this.channelActionSubmittingId = row.id;
            window.axios.post('/orders/' + row.id + '/channel-remind-collection').then(function (res) {
                _this.$message.success((res.data && res.data.message) || '催款提醒已发送');
                _this.fetchChannelCenterOrders();
            }).catch(function (err) {
                var msg = err && err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : '催款失败';
                _this.$message.error(msg);
            }).finally(function () {
                _this.channelActionSubmittingId = null;
            });
        },
        buildDefaultChannelForm() {
            return {
                name: '',
                managed_by_user_id: null,
                contact: '',
                phone: '',
                status: 1
            };
        },
        fetchChannelSpecialistUsers() {
            var _this = this;
            return window.axios
                .get('/channels/specialist-users')
                .then(function (res) {
                    var list = res.data && Array.isArray(res.data.data) ? res.data.data : [];
                    _this.channelSpecialistUsers = list;
                })
                .catch(function () {
                    _this.channelSpecialistUsers = [];
                });
        },
        fetchChannels() {
            var _this = this;
            this.channelsLoading = true;
            window.axios
                .get('/channels?active_only=1')
                .then(function (res) {
                    _this.channels = res.data && Array.isArray(res.data.data) ? res.data.data : [];
                })
                .catch(function () {
                    _this.channels = [];
                    _this.$message.error('渠道列表加载失败');
                })
                .finally(function () {
                    _this.channelsLoading = false;
                });
        },
        buildOrderQueryParams() {
            var p = {
                page: this.orderPage,
                per_page: this.orderPerPage
            };
            if (this.orderFilters.keyword) {
                p.keyword = this.orderFilters.keyword;
            }
            if (!this.isSubaccountUser && this.orderFilters.channel_id !== '' && this.orderFilters.channel_id != null) {
                p.channel_id = this.orderFilters.channel_id;
            }
            if (this.orderFilters.is_archived !== '' && this.orderFilters.is_archived !== null) {
                p.is_archived = this.orderFilters.is_archived;
            }
            if ((this.isSuperAdminUser || this.isChannelSpecialist) && this.orderFilters.settlement_status) {
                p.settlement_status = this.orderFilters.settlement_status;
                delete p.is_archived;
            }
            return p;
        },
        fetchChannelCenterOrders() {
            var _this = this;
            this.ordersLoading = true;
            window.axios
                .get('/channel-center/orders', { params: this.buildOrderQueryParams() })
                .then(function (res) {
                    _this.orders = res.data && Array.isArray(res.data.data) ? res.data.data : [];
                    var m = res.data && res.data.meta;
                    if (m) {
                        _this.orderMeta = {
                            current_page: Number(m.current_page != null ? m.current_page : 1),
                            last_page: Number(m.last_page != null ? m.last_page : 1),
                            per_page: Number(m.per_page != null ? m.per_page : _this.orderPerPage),
                            total: Number(m.total != null ? m.total : 0)
                        };
                    }
                    if (res.data && res.data.stats) {
                        _this.orderStats = res.data.stats;
                    }
                })
                .catch(function () {
                    _this.orders = [];
                    _this.$message.error('渠道订单加载失败');
                })
                .finally(function () {
                    _this.ordersLoading = false;
                });
        },
        runOrderSearch() {
            this.orderPage = 1;
            this.fetchChannelCenterOrders();
        },
        resetOrderFilters() {
            this.orderFilters = {
                keyword: '',
                channel_id: '',
                is_archived: '',
                settlement_status: ''
            };
            this.orderPage = 1;
            this.fetchChannelCenterOrders();
        },
        handleOrderPageChange(p) {
            this.orderPage = p;
            this.fetchChannelCenterOrders();
        },
        handleOrderPageSizeChange(size) {
            this.orderPerPage = size;
            this.orderPage = 1;
            this.fetchChannelCenterOrders();
        },
        openChannelFeeDialog(row) {
            if (!row || !row.id) {
                return;
            }
            if (!row.channel_id) {
                this.$message.warning('该订单未关联渠道');
                return;
            }
            var f = row.financial || {};
            this.channelFeeForm = {
                order_id: row.id,
                order_no: row.order_no || '',
                company_name: row.company && row.company.name ? row.company.name : '',
                channel_name: row.channel_name || (row.channel && row.channel.name) || '',
                channel_payable_total: f.channel_payable_total != null ? Number(f.channel_payable_total) : 0,
                channel_paid_total: f.channel_paid_total != null ? Number(f.channel_paid_total) : 0
            };
            this.channelFeeDialogVisible = true;
        },
        resetChannelFeeForm() {
            this.channelFeeForm = {
                order_id: null,
                order_no: '',
                company_name: '',
                channel_name: '',
                channel_payable_total: 0,
                channel_paid_total: 0
            };
        },
        submitChannelFees() {
            var _this = this;
            if (!this.channelFeeForm.order_id) {
                return;
            }
            var payable = Number(this.channelFeeForm.channel_payable_total || 0);
            var paid = Number(this.channelFeeForm.channel_paid_total || 0);
            if (paid > payable) {
                this.$message.warning(
                    this.isChannelSpecialist ? '已收渠道费不能大于应收渠道费' : '已付渠道费不能大于应付渠道费'
                );
                return;
            }
            this.channelFeeSaving = true;
            window.axios
                .put('/orders/' + this.channelFeeForm.order_id + '/channel-fees', {
                    channel_payable_total: payable,
                    channel_paid_total: paid
                })
                .then(function () {
                    _this.$message.success('渠道费用已保存');
                    _this.channelFeeDialogVisible = false;
                    _this.fetchChannelCenterOrders();
                })
                .catch(function (err) {
                    var msg =
                        err && err.response && err.response.data && err.response.data.message
                            ? err.response.data.message
                            : '保存失败';
                    _this.$message.error(msg);
                })
                .finally(function () {
                    _this.channelFeeSaving = false;
                });
        },
        confirmDeleteChannelOrder(row) {
            var _this = this;
            if (!row || !row.id) {
                return;
            }
            this.$confirm('删除后将彻底清理该订单及企业关联数据（若该企业下仅有本单）。是否继续？', '删除订单', {
                type: 'warning',
                confirmButtonText: '删除',
                cancelButtonText: '取消'
            })
                .then(function () {
                    return window.axios.delete('/orders/' + row.id);
                })
                .then(function () {
                    _this.$message.success('删除成功');
                    _this.fetchChannelCenterOrders();
                })
                .catch(function (err) {
                    if (err === 'cancel' || err === 'close') {
                        return;
                    }
                    var msg =
                        err && err.response && err.response.data && err.response.data.message
                            ? err.response.data.message
                            : '删除失败';
                    _this.$message.error(msg);
                });
        },
        openCreateChannelDialog() {
            var _this = this;
            this.channelDialogMode = 'create';
            this.editingChannelId = null;
            this.channelForm = this.buildDefaultChannelForm();
            this.fetchChannelSpecialistUsers().finally(function () {
                _this.channelDialogVisible = true;
            });
        },
        openEditChannelDialog(row) {
            var _this = this;
            if (!row || !row.id) {
                return;
            }
            this.channelDialogMode = 'edit';
            this.editingChannelId = row.id;
            this.channelForm = {
                name: row.name || '',
                managed_by_user_id:
                    row.managed_by_user_id !== undefined && row.managed_by_user_id !== null && row.managed_by_user_id !== ''
                        ? Number(row.managed_by_user_id)
                        : null,
                contact: row.contact || '',
                phone: row.phone || '',
                status: row.status !== undefined && row.status !== null ? Number(row.status) : 1
            };
            this.fetchChannelSpecialistUsers().finally(function () {
                _this.channelDialogVisible = true;
            });
        },
        resetChannelForm() {
            this.channelDialogMode = 'create';
            this.editingChannelId = null;
            this.channelForm = this.buildDefaultChannelForm();
        },
        submitChannelForm() {
            var _this = this;
            if (!this.channelForm.name) {
                this.$message.warning('请填写渠道名称');
                return;
            }
            var payload = {
                name: this.channelForm.name,
                contact: this.channelForm.contact || null,
                phone: this.channelForm.phone || null,
                status: this.channelForm.status
            };
            payload.managed_by_user_id =
                this.channelForm.managed_by_user_id !== null &&
                this.channelForm.managed_by_user_id !== undefined &&
                this.channelForm.managed_by_user_id !== ''
                    ? Number(this.channelForm.managed_by_user_id)
                    : null;
            this.channelSaving = true;
            var req =
                this.channelDialogMode === 'edit' && this.editingChannelId
                    ? window.axios.put('/channels/' + this.editingChannelId, payload)
                    : window.axios.post('/channels', payload);
            req
                .then(function (res) {
                    _this.$message.success((res.data && res.data.message) || '保存成功');
                    _this.channelDialogVisible = false;
                    _this.fetchChannels();
                })
                .catch(function (err) {
                    var msg =
                        err && err.response && err.response.data && err.response.data.message
                            ? err.response.data.message
                            : '保存失败';
                    _this.$message.error(msg);
                })
                .finally(function () {
                    _this.channelSaving = false;
                });
        },
        confirmDeleteChannel(row) {
            var _this = this;
            if (!row || !row.id) {
                return;
            }
            this.$confirm('删除后不可恢复，且仅当无关联订单时可删除。是否继续？', '删除渠道', {
                type: 'warning',
                confirmButtonText: '删除',
                cancelButtonText: '取消'
            })
                .then(function () {
                    return window.axios.delete('/channels/' + row.id);
                })
                .then(function () {
                    _this.$message.success('渠道已删除');
                    _this.fetchChannels();
                })
                .catch(function (err) {
                    if (err === 'cancel' || err === 'close') {
                        return;
                    }
                    var msg =
                        err && err.response && err.response.data && err.response.data.message
                            ? err.response.data.message
                            : '删除失败';
                    _this.$message.error(msg);
                });
        },
        syncTabFromRoute() {
            var q = this.$route && this.$route.query ? this.$route.query.tab : '';
            var map = {
                overview: 'orders',
                dashboard: 'orders',
                channels: 'channels',
                manage: 'channels',
                create: 'channels',
                orders: 'orders',
                settlement: 'orders',
                revenue: 'orders'
            };
            var name = map[String(q)] || 'orders';
            if (this.activeTab !== name) {
                this.activeTab = name;
            }
        },
        handleTabClick(tab) {
            var next = tab.name || 'orders';
            if (this.$route.query.tab === next) {
                return;
            }
            this.$router.replace({ name: 'channels', query: { tab: next } }).catch(function () {});
        },
        formatMoney(value) {
            var n = Number(value);
            if (Number.isNaN(n)) {
                return '0.00';
            }
            return n.toFixed(2);
        },
        formatDateTime(iso) {
            if (!iso) {
                return '—';
            }
            var s = String(iso);
            if (s.length >= 19) {
                return s.slice(0, 19).replace('T', ' ');
            }
            return s.slice(0, 16);
        },
        channelStatusLabel(row) {
            var s = row && row.status;
            if (s === 1 || s === '1') {
                return '启用';
            }
            if (s === 0 || s === '0') {
                return '停用';
            }
            if (typeof s === 'string' && s) {
                return s;
            }
            return '启用';
        },
        channelStatusTag(row) {
            var s = row && row.status;
            if (s === 0 || s === '0') {
                return 'info';
            }
            return 'success';
        }
    }
};
</script>

<style scoped>
.channel-center__tabs /deep/ .el-tabs__header {
    display: none;
}

.channel-center__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
}

.channel-center__search {
    width: 300px;
    max-width: 100%;
    margin-left: auto;
}

/* 避免满宽 + 右侧 fixed 在「待付」与「操作」之间出现空白列 */
.channel-center__table.app-hub-table {
    width: auto;
    max-width: 100%;
}

.channel-center__op-actions {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    gap: 2px;
}

.channel-center__amount-warn {
    color: #e6a23c;
    font-weight: 500;
}

.channel-center__danger {
    color: #f56c6c;
}

.channel-center__fee-action-locked.el-button--text,
.channel-center__fee-action-locked.el-button--text.is-disabled {
    color: #67c23a !important;
    -webkit-text-fill-color: #67c23a;
    cursor: not-allowed !important;
}

.channel-center__confirm-action-done.el-button--text,
.channel-center__confirm-action-done.el-button--text.is-disabled {
    color: #67c23a !important;
    -webkit-text-fill-color: #67c23a;
    cursor: not-allowed !important;
}

.channel-center__filter-input--wide {
    width: 280px;
}

.channel-center__filter-select {
    width: 220px;
}

.channel-center__filter-select--narrow {
    width: 120px;
}

.channel-center__fee-input {
    width: 100%;
}

.channel-center__fee-input >>> .el-input-number .el-input__inner {
    padding-left: 12px;
    padding-right: 12px;
    text-align: left;
}

.channel-center__fee-readonly >>> .el-input-number.is-disabled .el-input__inner {
    color: #303133;
    font-weight: 500;
    background-color: #f5f7fa;
    cursor: default;
    text-align: center;
}

.full-width {
    width: 100%;
}

.channel-center__managed-select {
    width: 100%;
}

.channel-center__field-tip {
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
    margin-top: 6px;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/views/ChannelCenterPage.vue